import React, { useMemo } from 'react';
import { arrayToObject } from 'utils/object-util';
import { Button, Paper } from '@material-ui/core';
import { useSummaryStepStyles } from './SummaryStepStyles';
import { SUMMARY_CARD_TYPES, SUMMARY_STATUS } from './SummaryStepConsts';
import { buildCreateActivityPricingValidationData, CREATE_ACTIVITY_PRICING_STEPS } from '../createActivityAndPricingConsts';
import { isStepModified } from '../utils/editMappingUtils';
import { useGetAllActivityTypesQuery } from 'features/activity/activitySlice';
import LoadingSpinner from 'components/LoadingSpinner';
import { useSelector } from 'react-redux';

const SummaryStep = ({ onEditStep }) => {
    const classes = useSummaryStepStyles();

    const steps = useSelector(state => state.createActivityAndPricingState.steps);
    const isEditMode = useSelector(state => state.createActivityAndPricingState.isEditMode);
    const originalSteps = useSelector(state => state.createActivityAndPricingState.originalSteps);

    const formData = buildCreateActivityPricingValidationData(steps);

    const { data: activityTypes, isLoading: isLoadingActivityTypes } = useGetAllActivityTypesQuery();

    const activityTypesById = useMemo(() => arrayToObject(activityTypes), [activityTypes]);

    const isModified = (stepIndex) => {
        if (!isEditMode) return false;
        return isStepModified(originalSteps, steps, stepIndex);
    };

    const formatActivityDetails = () => {
        const details = [];
        const activity = formData.activityDetails;

        if (activity?.name) {
            details.push(activity.name);
        }

        if (activity?.activityTypeId) {
            const activityType = activityTypesById[activity.activityTypeId];
            if (activityType) {
                details.push(activityType.name);
            }
        }

        if (activity?.noCharge) {
            details.push('No Charge');
        }

        if (activity?.paramTypes?.length > 0) {
            details.push(`Custom Fields: ${activity.paramTypes.length}`);
        }

        return details.join(' • ');
    };

    const formatPricingDetails = () => {
        const pricingCount = formData?.pricing?.pricingItems?.length || 0;
        const hasMaterials = formData?.pricing?.pricingItems?.some(item => item.priceConfig?.materialPrices?.length > 0);
        if (pricingCount === 0) return `${pricingCount}`;
        const base = `${pricingCount} pricing${pricingCount > 1 ? 's' : ''} configured`;
        return hasMaterials ? `${base} (with materials)` : base;
    };

    const formatAutomationDetails = () => {
        const rulesCount = formData?.rules?.rulesList?.length || 0;
        return rulesCount > 0
            ? `${rulesCount} rule${rulesCount > 1 ? 's' : ''} configured`
            : `${rulesCount}`;
    };

    if (isLoadingActivityTypes) {
        return <LoadingSpinner loading={true} />;
    }

    const isActivityConfigured = CREATE_ACTIVITY_PRICING_STEPS[0].validate(formData);
    const isPricingConfigured = CREATE_ACTIVITY_PRICING_STEPS[1].validate(formData);
    const isAutomationConfigured = CREATE_ACTIVITY_PRICING_STEPS[2].validate(formData);

    const hasAnyChanges =
        isEditMode &&
        (isModified(0) || isModified(1) || isModified(2));

    const summaryCards = [
        {
            title: SUMMARY_CARD_TYPES.ACTIVITY,
            status: isEditMode
                ? (isModified(0) ? SUMMARY_STATUS.MODIFIED : SUMMARY_STATUS.NOT_MODIFIED)
                : (isActivityConfigured ? SUMMARY_STATUS.CONFIGURED : SUMMARY_STATUS.SKIPPED),
            details: isActivityConfigured ? formatActivityDetails() : '',
            stepIndex: 0,
        },
        {
            title: SUMMARY_CARD_TYPES.PRICING,
            status: isEditMode
                ? (isModified(1) ? SUMMARY_STATUS.MODIFIED : SUMMARY_STATUS.NOT_MODIFIED)
                : (isPricingConfigured ? SUMMARY_STATUS.CONFIGURED : SUMMARY_STATUS.SKIPPED),
            details: isPricingConfigured ? formatPricingDetails() : '',
            stepIndex: 1,
        },
        {
            title: SUMMARY_CARD_TYPES.AUTOMATION,
            status: isEditMode
                ? (isModified(2) ? SUMMARY_STATUS.MODIFIED : SUMMARY_STATUS.NOT_MODIFIED)
                : (isAutomationConfigured ? SUMMARY_STATUS.CONFIGURED : SUMMARY_STATUS.SKIPPED),
            details: isAutomationConfigured ? formatAutomationDetails() : '',
            stepIndex: 2,
        },
    ];

    return (
        <div className={classes.container}>
            <div className={classes.headerSection}>
                <div className={classes.title}>
                    {isEditMode ? 'Ready to save' : 'Ready to create'}
                </div>
                <div className={classes.subtitle}>
                    {isEditMode
                        ? (hasAnyChanges
                            ? 'Review your changes before saving.'
                            : 'No changes have been made yet.')
                        : 'Review your configuration before creating the activity and pricing.'}
                </div>
            </div>

            <div className={classes.cardsList}>
                {summaryCards.map((card) => (
                    <Paper key={card.stepIndex} className={classes.summaryCard}>
                        <div className={classes.cardContent}>
                            <div className={classes.cardInfo}>
                                <div className={classes.cardHeader}>
                                    <span className={classes.cardTitle}>{card.title}</span>
                                    <span className={
                                        card.status === SUMMARY_STATUS.CONFIGURED
                                            ? classes.statusConfigured
                                            : card.status === SUMMARY_STATUS.MODIFIED
                                                ? classes.statusModified
                                                : card.status === SUMMARY_STATUS.NOT_MODIFIED
                                                    ? classes.statusNotModified
                                                    : classes.statusSkipped
                                    }>
                                        {card.status}
                                    </span>
                                </div>
                                <div className={classes.cardDetails}>
                                    {card.details || ''}
                                </div>
                            </div>
                            <Button
                                className={classes.editButton}
                                onClick={() => onEditStep(card.stepIndex)}
                            >
                                Edit
                            </Button>
                        </div>
                    </Paper>
                ))}
            </div>
        </div>
    );
};

export default SummaryStep;
